// The Create tab (DEVELOPMENT.md: Screens → Create): Generate first and open by default (Marko,
// 2026-11-02), then Build: the setter's drafts and ready puzzles, and "New puzzle". Rows use the
// library's look. `?tab=build` opens Build, for the way back from the builder.
import {
  SHAPES,
  shapeOf,
  type BuildsResponse,
  type DraftSummary,
  type NewDraftResponse,
  type ShapeKey,
} from '@zinc/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Page } from '../components/AppShell';
import { GeneratePanel } from '../components/GeneratePanel';
import { ScoreChip } from '../components/ScoreChip';
import { ShapeIcon } from '../components/ShapeIcon';
import { Thumbnail } from '../components/Thumbnail';
import { Card, EmptyState, ScreenHeader, SectionTitle, Segmented } from '../components/ui';
import { api } from '../lib/api';
import { typeName } from '../lib/library';

const SIZES = [
  { value: '4', label: '4×4' },
  { value: '6', label: '6×6' },
  { value: '8', label: '8×8' },
  { value: '9', label: '9×9' },
  { value: '12', label: '12×12' },
  { value: '16', label: '16×16' },
] as const;

function when(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 1)
    return `today, ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
  if (days < 2) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function Row({ p, last }: { p: DraftSummary; last: boolean }) {
  const types = [...new Set(p.constraintTypes.map((t) => typeName(t)))];
  const tags = [
    `${p.rows}×${p.cols}`,
    ...types.slice(0, 2),
    ...(types.length > 2 ? [`+${types.length - 2}`] : []),
  ].join(' · ');
  return (
    <li
      className={`flex items-center gap-3 py-2.5 pr-3 pl-3 ${last ? '' : 'border-b border-row-line'}`}
    >
      <Thumbnail doc={p.document} size={48} />
      <Link
        to={p.status === 'ready' ? `/library/${p.id}` : `/build/${p.id}`}
        className="flex min-w-0 flex-1 flex-col gap-0.5 text-ink no-underline hover:text-ink"
      >
        <span className="truncate text-[15px] font-semibold">{p.title}</span>
        <span className="truncate text-[12px] text-muted">{tags}</span>
        <span className="text-[12px] text-muted">
          {p.status === 'ready' ? 'In the library' : `Edited ${when(p.updatedAt)}`}
        </span>
      </Link>
      {p.status === 'ready' && <ScoreChip score={p.score} />}
    </li>
  );
}

function NewPuzzleDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [size, setSize] = useState<string>('9');
  const [regions, setRegions] = useState<'boxes' | 'irregular'>('boxes');
  const [shape, setShape] = useState<ShapeKey | null>(null);
  const [title, setTitle] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api<NewDraftResponse>('/api/builds', {
        method: 'POST',
        json: {
          size: Number(size),
          irregular: !shape && regions === 'irregular',
          ...(shape ? { shape } : {}),
          title: title.trim() || undefined,
        },
      }),
    onSuccess: (r) => void navigate(`/build/${r.id}`),
  });
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[rgba(31,30,28,0.35)] p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-puzzle-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[calc(100dvh-24px)] w-full max-w-[420px] flex-col gap-3 overflow-y-auto rounded-[20px] border border-line bg-surface px-5 py-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="new-puzzle-title" className="m-0 text-[20px] font-bold">
          New puzzle
        </h2>
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-muted">Title</span>
          <input
            value={title}
            maxLength={200}
            placeholder="Untitled"
            onChange={(e) => setTitle(e.target.value)}
            className="h-11 rounded-[12px] border border-line bg-field px-3 text-[15px] text-ink"
          />
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-muted">Shape</span>
          <div role="radiogroup" aria-label="Shape" className="grid grid-cols-3 gap-1.5">
            {[null, ...SHAPES].map((s) => {
              const on = (s?.key ?? null) === shape;
              return (
                <button
                  key={s?.key ?? 'one'}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => {
                    setShape(s?.key ?? null);
                    // A shape comes in some sizes: 9×9 when the one picked isn't one.
                    if (s && !s.sizes.includes(Number(size))) setSize('9');
                  }}
                  className={`flex min-h-[76px] flex-col items-center justify-center gap-1 rounded-[12px] border text-[13px] ${on ? 'border-accent bg-accent-tint font-semibold text-accent-dark' : 'border-transparent bg-field text-secondary'}`}
                >
                  <ShapeIcon shape={s?.key ?? null} />
                  <span className="text-ink">{s?.name ?? 'One grid'}</span>
                </button>
              );
            })}
          </div>
          {shape && (
            <p className="mx-1 my-0 text-[12px] text-muted">{shapeOf(shape)!.note(Number(size))}</p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-muted">Size</span>
          <Segmented
            label="Size"
            value={size}
            onChange={setSize}
            options={SIZES.map((o) => ({
              ...o,
              disabled: shape !== null && !shapeOf(shape)!.sizes.includes(Number(o.value)),
            }))}
          />
          {shape && (
            <p className="mx-1 my-0 text-[12px] text-muted">The size of each of its grids.</p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-muted">Regions</span>
          <Segmented
            label="Regions"
            value={shape ? 'boxes' : regions}
            onChange={setRegions}
            options={[
              { value: 'boxes', label: 'Boxes' },
              { value: 'irregular', label: 'Irregular', disabled: shape !== null },
            ]}
          />
          {!shape && (
            <p className="mx-1 my-0 text-[12px] text-muted">
              {regions === 'irregular'
                ? 'Starts from the boxes; redraw them with Irregular in the builder.'
                : 'You can make it irregular later in the builder.'}
            </p>
          )}
        </div>
        {create.isError && (
          <p role="alert" className="m-0 text-[14px] text-mistake">
            That didn’t work. Check your connection and try again.
          </p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-12 flex-1 rounded-[12px] border border-line bg-surface text-[15px] font-semibold text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={create.isPending}
            onClick={() => create.mutate()}
            className="h-12 flex-1 rounded-[12px] border-0 bg-accent text-[15px] font-bold text-white disabled:opacity-45"
          >
            Start building
          </button>
        </div>
      </div>
    </div>
  );
}

function Drafts() {
  const builds = useQuery({
    queryKey: ['builds'],
    queryFn: () => api<BuildsResponse>('/api/builds'),
  });
  const drafts = builds.data?.drafts ?? [];
  const ready = builds.data?.ready ?? [];
  return (
    <>
      {builds.isPending ? null : drafts.length === 0 && ready.length === 0 ? (
        <EmptyState
          title="Your drafts"
          body="Start a new puzzle: draw it yourself, let the app suggest clues where the logic stalls, or let it make whole puzzles to pick from. Drafts stay private until you mark them ready."
        />
      ) : (
        <>
          <SectionTitle>Drafts</SectionTitle>
          {drafts.length === 0 ? (
            <p className="mx-1 my-0 text-[14px] text-muted">No drafts. Start a new puzzle.</p>
          ) : (
            <Card>
              <ul aria-label="Drafts" className="m-0 list-none p-0">
                {drafts.map((p, i) => (
                  <Row key={p.id} p={p} last={i === drafts.length - 1} />
                ))}
              </ul>
            </Card>
          )}
          {ready.length > 0 && (
            <>
              <SectionTitle>Ready</SectionTitle>
              <Card>
                <ul aria-label="Ready puzzles" className="m-0 list-none p-0">
                  {ready.map((p, i) => (
                    <Row key={p.id} p={p} last={i === ready.length - 1} />
                  ))}
                </ul>
              </Card>
            </>
          )}
        </>
      )}
    </>
  );
}

export function BuildPage() {
  const [creating, setCreating] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'build' ? 'build' : 'generate';
  return (
    <>
      <ScreenHeader
        title="Create"
        actions={
          tab === 'build' ? (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="mr-1.5 h-10 rounded-btn border-0 bg-accent px-4 text-[14px] font-semibold text-white"
            >
              New puzzle
            </button>
          ) : undefined
        }
      />
      <Page>
        <Segmented
          label="Create"
          value={tab}
          onChange={(v) => setParams(v === 'build' ? { tab: 'build' } : {}, { replace: true })}
          options={[
            { value: 'generate', label: 'Generate' },
            { value: 'build', label: 'Build' },
          ]}
        />
        {tab === 'generate' ? <GeneratePanel /> : <Drafts />}
      </Page>
      {creating && <NewPuzzleDialog onClose={() => setCreating(false)} />}
    </>
  );
}
